/**
 * Add-to-cart — what clients POST to `/api/cart`.
 *
 * The route upserts on (listing_id, user|guest): adding a listing already in
 * the cart INCREMENTS the line quantity (capped at available stock) and still
 * returns `{ success: true }` — there is no "already in cart" rejection.
 * The only typed failure is `code: "ITEM_UNAVAILABLE"` (stock exhausted by
 * other buyers' reservations or sold out).
 *
 * This request predates the contract package and was the last cart mutation
 * without a pin (parity audit P3, 2026-07-05) — mobile shipped a hand-rolled
 * `Record<string, unknown>` body, the drift class that killed the capture
 * channel. Producers `satisfies AddToCartRequest`.
 *
 * @see app/api/cart/route.ts POST — consumer (field coercion is lenient
 *   server-side; this schema pins the shape well-behaved clients send)
 * @see lib/cart-perform-add.ts — upsert + stock-cap semantics
 * @see apps/mobile/lib/api-kraahl.ts addListingToCartRequest — producer
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface AddToCartRequest {
  listingId: string;
  qty: number;
  /** Exactly one of userId/guestId identifies the cart owner. */
  userId?: string | null;
  guestId?: string | null;
  /** Accepted-offer settlement — the line carries the offer linkage. */
  offerId?: string | null;
  /** Chat-private purchase context (chat room + message provenance). */
  roomName?: string | null;
  messageId?: string | null;
}

export const AddToCartRequestSchema = pinned<AddToCartRequest>()(
  z.object({
    listingId: z.string().min(1),
    qty: z.number().int().min(1),
    userId: z.string().nullable().optional(),
    guestId: z.string().nullable().optional(),
    offerId: z.string().nullable().optional(),
    roomName: z.string().nullable().optional(),
    messageId: z.string().nullable().optional(),
  }),
);

/**
 * Typed failure codes on POST /api/cart error envelopes.
 *
 * Every rejection is now a code, not a message a client has to regex. The
 * route used to substring-match the thrown message (`includes("currently
 * unavailable")`), so only the stock case ever reached a client as a code.
 * Producer: `describeAddToCartRejection` in `lib/cart/add-to-cart-rejection.ts`
 * — one mapper, so the HTTP route and the server action cannot disagree.
 *
 * `ITEM_UNAVAILABLE` keeps its exact spelling: clients branch on it today.
 *
 * @see lib/cart/add-to-cart-rejection.ts — the union these codes project from
 */
export type AddToCartErrorCode =
  | "ITEM_UNAVAILABLE"
  | "LISTING_NOT_FOUND"
  | "LISTING_UNAVAILABLE"
  | "LISTING_CANCELLED"
  | "SELLER_UNAVAILABLE"
  | "CHAT_GRANT_MISSING"
  | "UNAUTHENTICATED"
  | "NO_CART_IDENTITY"
  | "INVALID_QUANTITY";
